import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack, Section } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import type { TemplateOnlyComponent } from '@ember/component/template-only';
import type { ComponentLike } from '@glint/template';
import { formatISODateToDisplay } from 'tio-common/utils/date';
import CurrencySuperscriptCents from 'tio-common/components/currency-superscript-cents';
import formatCentsToDollars from 'tio-common/helpers/format-cents-to-dollars';
import BenefitSummaryCards from 'tio-common/components/tas/benefit-summary-cards';
import { htmlSafe } from '@ember/template';
import Chip from 'tio-ui/components/chip';
import Select from 'tio-ui/components/select';
import Button from 'tio-ui/components/button';
import { t } from 'ember-intl';
import type { IntlService } from 'ember-intl';
import {
  PhArrowRight,
  PhShieldCheck,
  PhBookOpen,
  PhGraduationCap,
  PhLock,
  PhBank,
  PhCompass,
} from 'ember-phosphor-icons';
import PageHero from 'tio-ui/components/page-hero';
import Card from 'tio-ui/components/card';
import ActivityCard from 'tio-employee/components/tas/activity-card';
import AddApplicationButton from 'tio-employee/components/tas/add-application/button';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type SessionContextService from 'tio-employee/services/session-context';
import type PartnerService from 'tio-employee/services/partner';
import type DemoModeService from 'tio-employee/services/demo-mode';
import { MANAGE_ROUTE } from 'tio-employee/utils/tas/manage-link';
import { sortEligibleTemplates } from 'tio-employee/utils/tas/eligible-benefits-sort';
import type { EligibleSort } from 'tio-employee/utils/tas/eligible-benefits-sort';
import TasDashboardLpnPromo from 'tio-employee/components/tas/dashboard/lpn-promo';
import {
  isActiveInstanceState,
  isActiveApplicationState,
} from 'tio-common/utils/tuition-assistance/application-states';
import type {
  DataSourceInfo,
  EligibleTemplate,
  LiveTasApplication,
  LiveTasData,
  LiveTasProgram,
} from 'tio-employee/services/reimagined-data';
import type { LimitsStatusDataSignature } from 'tio-common/services/tuition-assistance';
import pageTitle from 'ember-page-title/helpers/page-title';

/**
 * Ineligible "explore" card — icon-chip + heading + body + trailing
 * arrow inside an elevated white card. Co-located here (2 call sites
 * in the ineligible branch — SLR and All Benefits).
 */
type ExploreIntent = 'brand' | 'success';
// Matches the canonical phosphor icon signature in
// `test-app/types/ember-phosphor-icons.d.ts` so Glint accepts every
// PhFoo without complaining about missing optional args (class/weight).
type IconComponent = ComponentLike<{
  Args: {
    size?: number | string;
    weight?: string;
    class?: string;
  };
  Element: SVGElement;
}>;

interface ExploreCardSignature {
  Args: {
    icon: IconComponent;
    intent: ExploreIntent;
    titleKey: string;
    bodyKey: string;
  };
}

const ExploreCard: TemplateOnlyComponent<ExploreCardSignature> = <template>
  <div class="app-elevated-card rounded-2xl p-6 hover:shadow-md transition-shadow">
    <div class="tio-row-loose">
      <div class="app-icon-chip app-icon-chip--{{@intent}}">
        <@icon @size={{24}} />
      </div>
      <div>
        <Heading @level={{3}}>{{t @titleKey}}</Heading>
        <Text @variant="muted">{{t @bodyKey}}</Text>
      </div>
      <PhArrowRight class="tio-icon-md ml-auto app-text-disabled" />
    </div>
  </div>
</template>;

interface TasDashboardModel {
  tasData: LiveTasData;
  tasUsedAmount: { yearToDate: number; isLiveData: boolean };
  dataSourceInfo: DataSourceInfo;
  eligibleTemplates: EligibleTemplate[];
  hasCreditCapacity: boolean;
  limitsStatus?: LimitsStatusDataSignature;
}

const PAID_APPLICATION_STATES = new Set([
  'TAS.ApplicationPaymentState.PAID',
  'TAS.ApplicationPaymentState.PAID_IN_FULL',
]);

const PENDING_APPLICATION_STATES = new Set([
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
]);

const REQUESTED_APPLICATION_STATES = new Set([
  'TAS.ApplicationState.CREATED',
  'TAS.ApplicationState.DEFINE_COURSES',
]);

/** Terminal negative states — hide after 1 month */
const STALE_FILTERABLE_STATES = new Set([
  'TAS.ApplicationState.COURSES_REJECTED',
  'TAS.ApplicationState.EVIDENCE_REJECTED',
  'TAS.ApplicationState.REJECTED_PER_POLICY',
  'TAS.ApplicationState.ABANDONED',
]);

/** Payment states that indicate the application has been paid */
const PAID_PAYMENT_STATES = new Set([
  'TAS.ApplicationPaymentState.PAID',
  'TAS.ApplicationPaymentState.PAID_IN_FULL',
  'TAS.ApplicationPaymentState.PAID_IN_PART',
]);

/** Payment state indicating active repayment — always shown */
const REPAYMENT_PAYMENT_STATE = 'TAS.ApplicationPaymentState.REFUND_REQUESTED';

/** Program instance states where the employee cannot yet add requests */
const PENDING_PROGRAM_STATES = new Set([
  'TAS.ProgramInstanceState.CREATED',
  'TAS.ProgramInstanceState.DRAFT',
  'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL',
]);

function canAddRequests(state: string) {
  return !PENDING_PROGRAM_STATES.has(state);
}

/** Type 4 programs — only these show as standalone program cards in Activity */
const TYPE_4_CLASSIFICATIONS = new Set(['TAS.ProgramType.4.2', 'TAS.ProgramType.4.3']);
const CREDITS_CAPACITY = 'TAS.LimitCapacity.CREDITS';

export interface S {
  Args: {
    model: TasDashboardModel;
  };
}

type ActivityItem =
  | { kind: 'application'; data: LiveTasApplication; sortDate: string }
  | { kind: 'program'; data: LiveTasProgram; sortDate: string };

export default class TasDashboard extends Component<S> {
  @service declare reimaginedData: ReimaginedDataService;
  @service declare intl: IntlService;
  @service declare sessionContext: SessionContextService;
  @service declare partner: PartnerService;
  @service declare demoMode: DemoModeService;

  get isCreditBased() {
    return this.args.model.limitsStatus?.limit_capacity === CREDITS_CAPACITY;
  }

  formatCredits = (amount: number) => {
    return this.intl.t('tas.dashboard.annual_benefits.credits_count', { count: amount });
  };

  get currentEmployeeId() {
    return this.sessionContext.currentEmployee?.id ?? '';
  }

  get legacyAnnualStartDate() {
    return this.args.model.limitsStatus?.annual_start_date || '';
  }

  get legacyAnnualEndDate() {
    return this.args.model.limitsStatus?.annual_end_date || '';
  }

  // Credit-capacity programs keep this legacy status-based card; monetary
  // programs render the tree-sourced BenefitSummaryCards below instead.
  get showLegacyBenefitCard() {
    return Boolean(this.args.model.hasCreditCapacity && this.args.model.limitsStatus);
  }

  get externalSpendLabel() {
    return (
      this.partner.settingModules?.externalSpendDescriptor ||
      this.intl.t('tas.dashboard.annual_benefits.external')
    );
  }

  get greetingText() {
    const hour = new Date().getHours();
    let base: string;
    if (hour < 12) {
      base = this.intl.t('tuition_assistance.reimagined.dashboard.greeting_morning');
    } else if (hour < 17) {
      base = this.intl.t('tuition_assistance.reimagined.dashboard.greeting_afternoon');
    } else {
      base = this.intl.t('tuition_assistance.reimagined.dashboard.greeting_evening');
    }
    const name = this.firstName;
    return name ? `${base}, ${name}` : base;
  }

  get firstName() {
    return this.sessionContext.currentEmployee?.firstName || '';
  }

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get isDependentProgramsEnabled() {
    return (
      this.sessionContext.currentEmployee?.company?.companySetting?.tuitionAssistance
        ?.isDependentProgramsEnabled || false
    );
  }

  get isEligible() {
    return this.args.model.tasData.isEligible;
  }

  get tasSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.args.model.tasData.isLiveData ? 'live' : 'placeholder';
  }

  get programs(): LiveTasProgram[] {
    return this.args.model.tasData.programs.filter(
      (p) =>
        isActiveInstanceState(p.state) &&
        p.typeClassification !== null &&
        TYPE_4_CLASSIFICATIONS.has(p.typeClassification)
    );
  }

  get primedPrograms(): LiveTasProgram[] {
    return this.programs.filter((p) => p.state === 'TAS.ProgramInstanceState.PRIMED');
  }

  get applications(): LiveTasApplication[] {
    return this.args.model.tasData.applications.filter((app) => app.recordType === 'application');
  }

  get totalApproved() {
    return this.args.model.tasData.summary.totalApproved;
  }

  get totalPaid() {
    return this.args.model.tasData.summary.totalPaid;
  }

  get eligibleTemplates(): EligibleTemplate[] {
    return this.args.model.eligibleTemplates ?? [];
  }

  @tracked eligibleSort: EligibleSort = 'date';

  get eligibleSortOptions() {
    return [
      { key: 'date', label: this.intl.t('tas.dashboard.sort_options.by_date') },
      { key: 'alphabetical', label: this.intl.t('tas.dashboard.sort_options.alphabetically') },
    ];
  }

  get sortedEligibleTemplates() {
    return sortEligibleTemplates(this.eligibleTemplates, this.eligibleSort);
  }

  handleEligibleSortChange = (key: string) => {
    this.eligibleSort = key as EligibleSort;
  };

  /**
   * Count of applications awaiting payment (approved but not yet paid)
   */
  get awaitingPayment() {
    return this.totalApproved - this.totalPaid;
  }

  /**
   * Count of applications in review (PENDING_COURSES_APPROVAL or PENDING_EVIDENCE_APPROVAL)
   */
  get inReviewCount() {
    return this.applications.filter((app) => PENDING_APPLICATION_STATES.has(app.state)).length;
  }

  /**
   * Correctly count active items based on program type:
   * - Type 2, 3: Count only active applications (ignore program instance)
   * - Type 1, 4.2, 4.3: Count active instances + active applications
   * - Active instances with no child applications (e.g. Draft) are also counted
   */
  @cached
  get properActiveCount() {
    // Pre-group applications by programInstanceId to avoid repeated full scans
    const appsByProgramInstance = new Map<string, LiveTasApplication[]>();
    const activeApplications = this.args.model.tasData.applications.filter(
      (app) => app.recordType === 'application' && isActiveApplicationState(app.state)
    );

    for (const app of activeApplications) {
      if (!app.programInstanceId) continue;
      let list = appsByProgramInstance.get(app.programInstanceId);
      if (!list) {
        list = [];
        appsByProgramInstance.set(app.programInstanceId, list);
      }
      list.push(app);
    }

    // Build a map of programTemplateId -> typeClassification from programs
    const templateTypeMap = new Map<string, string>();

    for (const program of this.args.model.tasData.programs) {
      if (program.typeClassification && isActiveInstanceState(program.state)) {
        const allAppsByInstance = appsByProgramInstance.get(program.id) ?? [];
        for (const app of allAppsByInstance) {
          if (app.programTemplateId) {
            templateTypeMap.set(app.programTemplateId, program.typeClassification);
          }
        }
      }
    }

    let count = 0;

    // Group applications by template type
    const type2or3Apps: LiveTasApplication[] = [];
    const type1or4Apps: LiveTasApplication[] = [];
    const unknownTypeApps: LiveTasApplication[] = [];

    for (const app of activeApplications) {
      const templateId = app.programTemplateId;
      if (!templateId) {
        continue;
      }

      const typeClassification = templateTypeMap.get(templateId);
      if (
        typeClassification === 'TAS.ProgramType.2' ||
        typeClassification === 'TAS.ProgramType.3'
      ) {
        type2or3Apps.push(app);
      } else if (
        typeClassification === 'TAS.ProgramType.1' ||
        typeClassification === 'TAS.ProgramType.4.2' ||
        typeClassification === 'TAS.ProgramType.4.3'
      ) {
        type1or4Apps.push(app);
      } else {
        unknownTypeApps.push(app);
      }
    }

    // Type 2/3: Count only active applications
    count += type2or3Apps.length;

    // Type 1/4.2/4.3: Count active instances + active applications
    const instancesWithApps = new Set(
      type1or4Apps.map((app) => app.programInstanceId).filter((id): id is string => id !== null)
    );
    count += instancesWithApps.size; // Count unique instances that have apps
    count += type1or4Apps.length; // Count applications

    // Count active program instances that have NO child applications (e.g. Draft instances)
    // These are instances in active states where the employee hasn't started an application yet
    const instancesWithAnyApps = new Set(
      this.args.model.tasData.applications
        .filter((app) => app.recordType === 'application' && app.programInstanceId)
        .map((app) => app.programInstanceId)
    );
    const standaloneActiveInstances = this.args.model.tasData.programs.filter(
      (p) => isActiveInstanceState(p.state) && !instancesWithAnyApps.has(p.id)
    );
    count += standaloneActiveInstances.length;

    // Fallback for unknown types: count applications only
    count += unknownTypeApps.length;

    return count;
  }

  /**
   * Count of completed applications
   */
  get completedCount() {
    return this.args.model.tasData.summary.completedCount;
  }

  /**
   * Whether there are any active programs or applications
   * Used to determine which hero layout to show.
   * Checks ALL active program types (not just Type 4) so that Type 1
   * instances also inform the hero display.
   */
  get hasActiveActivity() {
    const hasActivePrograms = this.args.model.tasData.programs.some((p) =>
      isActiveInstanceState(p.state)
    );
    return hasActivePrograms || this.applications.length > 0;
  }

  /**
   * Unified activity list merging applications + standalone program instances.
   * Sorted by updatedAt descending (most recent first).
   *
   * Visibility rules:
   * - In repayment (REFUND_REQUESTED) → always shown
   * - Rejected / abandoned → shown only if last action < 1 month
   * - Fulfilled + paid → shown only if last action < 1 month
   * - Fulfilled + not paid → always shown (still active)
   * - All other states → always shown
   */
  get activityItems() {
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    const visibleApps = this.applications.filter((app) => {
      // In repayment → always show
      if (app.paymentState === REPAYMENT_PAYMENT_STATE) return true;

      // Rejected / abandoned → time-limited
      if (STALE_FILTERABLE_STATES.has(app.state)) {
        const lastAction = app.updatedAt ?? app.createdAt;
        return new Date(lastAction) >= oneMonthAgo;
      }

      // Fulfilled + paid → time-limited
      if (
        app.state === 'TAS.ApplicationState.FULFILLED' &&
        PAID_PAYMENT_STATES.has(app.paymentState)
      ) {
        const lastAction = app.updatedAt ?? app.createdAt;
        return new Date(lastAction) >= oneMonthAgo;
      }

      // Everything else (including Fulfilled + not paid) → always show
      return true;
    });

    // Collect IDs of program instances that already have applications
    const instancesWithApps = new Set(
      visibleApps.map((app) => app.programInstanceId).filter((id): id is string => id !== null)
    );

    // Program instances with NO child applications
    const standalonePrograms = this.programs.filter((p) => !instancesWithApps.has(p.id));

    const items: Array<
      | { kind: 'application'; data: LiveTasApplication; sortDate: string }
      | { kind: 'program'; data: LiveTasProgram; sortDate: string }
    > = [];

    for (const app of visibleApps) {
      items.push({
        kind: 'application',
        data: app,
        sortDate: app.updatedAt ?? app.createdAt,
      });
    }

    for (const program of standalonePrograms) {
      items.push({
        kind: 'program',
        data: program,
        sortDate: program.updatedAt ?? '',
      });
    }

    // Sort by most recent first — compare parsed instants, not strings:
    // serialized offsets flip across DST (-05:00/-06:00) within one list,
    // breaking lexicographic order. Missing dates (epoch) sort last.
    items.sort((a, b) => new Date(b.sortDate || 0).getTime() - new Date(a.sortDate || 0).getTime());

    return items;
  }

  /**
   * Count of items currently in review
   */
  get tasksRequiredCount() {
    return this.inReviewCount;
  }

  /**
   * Calculate progress percentage for the benefit usage
   */
  get benefitUsagePercentage() {
    const limitsStatus = this.args.model.limitsStatus;
    if (!limitsStatus) return 0;

    const usage = limitsStatus.details?.batch?.annual_usage ?? 0;
    const limit = limitsStatus.details?.batch?.annual_limit ?? 0;

    if (limit <= 0) return 0;
    return Math.min(100, Math.round((usage / limit) * 100));
  }

  /**
   * Annual limit (in cents)
   * Uses the highest value across all levels (batch, scheme, product, company)
   */
  get annualLimit() {
    const limitsStatus = this.args.model.limitsStatus;
    if (!limitsStatus) return 0;

    const batchLimit = limitsStatus.details?.batch?.annual_limit ?? 0;
    const schemeLimit = limitsStatus.details?.scheme?.annual_limit ?? 0;
    const productLimit = limitsStatus.details?.product?.annual_limit ?? 0;
    const companyLimit = limitsStatus.details?.company?.annual_limit ?? 0;

    return Math.max(batchLimit, schemeLimit, productLimit, companyLimit);
  }

  /**
   * Net paid amount (in cents)
   * Uses the highest value across all levels (batch, scheme, product, company).
   */
  get paidAmount() {
    const limitsStatus = this.args.model.limitsStatus;
    if (!limitsStatus) return 0;

    const batchUsage = limitsStatus.details?.batch?.annual_usage ?? 0;
    const schemeUsage = limitsStatus.details?.scheme?.annual_usage ?? 0;
    const productUsage = limitsStatus.details?.product?.annual_usage ?? 0;
    const companyUsage = limitsStatus.details?.company?.annual_usage ?? 0;

    return Math.max(batchUsage, schemeUsage, productUsage, companyUsage);
  }

  /**
   * External spend from limits service (in cents)
   * Uses the highest value across all levels (batch, scheme, product, company).
   * Includes root-level external usage when present.
   */
  get externalAmount() {
    const limitsStatus = this.args.model.limitsStatus;
    if (!limitsStatus) return 0;

    const batchExternal = limitsStatus.details?.batch?.external_annual_usage ?? 0;
    const schemeExternal = limitsStatus.details?.scheme?.external_annual_usage ?? 0;
    const productExternal = limitsStatus.details?.product?.external_annual_usage ?? 0;
    const companyExternal = limitsStatus.details?.company?.external_annual_usage ?? 0;
    const rootExternal = limitsStatus.external_annual_usage ?? 0;

    return Math.max(batchExternal, schemeExternal, productExternal, companyExternal, rootExternal);
  }

  /**
   * Total used = paid + external (no pending)
   */
  get totalUsed() {
    return this.paidAmount + this.externalAmount;
  }

  /**
   * Whether there is a limit
   */
  get hasLimit() {
    return this.annualLimit > 0;
  }

  // No visibility floor here: a 2px min-width on the segments keeps a tiny spend
  // visible without the bar claiming a share of the cap that was never used.
  get totalUsedPercentage() {
    if (!this.hasLimit || this.totalUsed <= 0) return 0;
    return Math.min(100, (this.totalUsed / this.annualLimit) * 100);
  }

  /**
   * Paid segment percentage
   */
  get paidPercentage() {
    if (!this.hasLimit || this.paidAmount <= 0 || this.totalUsed <= 0) return 0;
    return (this.paidAmount / this.totalUsed) * this.totalUsedPercentage;
  }

  /**
   * External segment percentage
   */
  get externalPercentage() {
    if (!this.hasLimit || this.externalAmount <= 0 || this.totalUsed <= 0) return 0;
    return (this.externalAmount / this.totalUsed) * this.totalUsedPercentage;
  }

  /**
   * Get remaining amount
   */
  get remainingAmount() {
    const limitsStatus = this.args.model.limitsStatus;
    if (!limitsStatus) return 0;

    const available = limitsStatus.available;
    if (available !== null && available !== undefined) {
      return Math.max(0, available);
    }

    if (!this.annualLimit) {
      return 0;
    }
    return Math.max(0, this.annualLimit - this.totalUsed);
  }

  /**
   * Style helpers for progress bar segments
   */
  get paidStyle() {
    return htmlSafe(`width: ${this.paidPercentage}%; min-width: 2px`);
  }

  get externalStyle() {
    return htmlSafe(`width: ${this.externalPercentage}%; min-width: 2px`);
  }

  applicationAmountInfo = (application: LiveTasApplication) => {
    const isRequestedState = REQUESTED_APPLICATION_STATES.has(application.state);
    const reimbursementApprovedTotal = application.approvedTotal;
    const reimbursementRequestedTotal = application.requestedTotal;

    const courseCashApprovedTotal = isRequestedState
      ? (application.courseCashPreApprovedTotal ?? application.courseCashFinalApprovedTotal ?? 0)
      : (application.courseCashFinalApprovedTotal ?? application.courseCashPreApprovedTotal ?? 0);

    const courseCashUsed = application.courseCashUsed ?? 0;
    const courseCashReserved = application.courseCashReserved ?? 0;

    // Only consider it a course cash app if there's an approved amount OR actual usage/reserved amounts
    const isCourseCashApp =
      courseCashApprovedTotal > 0 || courseCashUsed > 0 || courseCashReserved > 0;

    // CourseCash display: used > reserved > pre-approved (requested)
    let courseCashAmount = courseCashUsed;
    let courseCashLabelKey = 'tuition_assistance.reimagined.index.application_amount_used';
    if (courseCashUsed <= 0 && courseCashReserved > 0) {
      courseCashAmount = courseCashReserved;
      courseCashLabelKey = 'tuition_assistance.reimagined.index.application_amount_reserved';
    } else if (courseCashUsed <= 0 && courseCashReserved <= 0 && courseCashApprovedTotal > 0) {
      courseCashAmount = courseCashApprovedTotal;
      courseCashLabelKey = 'tuition_assistance.reimagined.index.application_amount_requested';
    }

    if (application.paymentState === 'TAS.ApplicationPaymentState.REFUND_REQUESTED') {
      const amount = application.approvedTotal;
      if (amount <= 0) return null;
      return {
        reimbursementAmount: amount,
        reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_repayment',
        courseCashAmount,
        courseCashLabelKey,
        hasCourseCash: isCourseCashApp,
      };
    }

    if (
      PAID_APPLICATION_STATES.has(application.paymentState) ||
      application.paymentState === 'TAS.ApplicationPaymentState.PAID_IN_PART'
    ) {
      const amount = application.paidTotal || application.approvedTotal;
      if (amount <= 0) return null;
      return {
        reimbursementAmount: amount,
        reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_paid',
        courseCashAmount,
        courseCashLabelKey,
        hasCourseCash: isCourseCashApp,
      };
    }

    if (PENDING_APPLICATION_STATES.has(application.state)) {
      const amount = reimbursementApprovedTotal;
      if (amount <= 0 && reimbursementRequestedTotal <= 0 && !isCourseCashApp) return null;
      return {
        reimbursementAmount: amount > 0 ? amount : reimbursementRequestedTotal,
        reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_requested',
        courseCashAmount,
        courseCashLabelKey,
        hasCourseCash: isCourseCashApp,
      };
    }

    // Fallback: approved > requested
    const reimbursementAmount =
      reimbursementApprovedTotal > 0 ? reimbursementApprovedTotal : reimbursementRequestedTotal;
    if (reimbursementAmount <= 0 && !isCourseCashApp) return null;

    return {
      reimbursementAmount,
      reimbursementLabelKey:
        reimbursementApprovedTotal > 0
          ? 'tuition_assistance.reimagined.index.application_amount_approved'
          : 'tuition_assistance.reimagined.index.application_amount_requested',
      courseCashAmount,
      courseCashLabelKey,
      hasCourseCash: isCourseCashApp,
    };
  };

  itemProgramName = (item: ActivityItem): string | null =>
    item.kind === 'application' ? item.data.programName : null;

  itemAmountInfo = (item: ActivityItem) =>
    item.kind === 'application' ? this.applicationAmountInfo(item.data) : null;

  itemInstitution = (item: ActivityItem): string | null =>
    item.kind === 'program' ? item.data.institution : null;

  itemPaymentState = (item: ActivityItem): string | null =>
    item.kind === 'application' && item.data.paymentState === REPAYMENT_PAYMENT_STATE
      ? item.data.paymentState
      : null;

  /**
   * Where an activity card links. Every application opens the manage screen —
   * Shares MANAGE_ROUTE with every other TAS entry point.
   *
   * The program branch returns the same route its own card hardcodes below — the
   * template only calls this from the application branch, but `ActivityItem` is a
   * union and Glint does not narrow it through `{{#if}}`.
   */
  itemDetailRoute = (item: ActivityItem): string =>
    item.kind === 'application' ? MANAGE_ROUTE : 'authenticated.tas.programs.show';

  /** Count applications belonging to a given program instance */
  programRequestCount = (programId: string): number =>
    this.args.model.tasData.applications.filter(
      (app) => app.recordType === 'application' && app.programInstanceId === programId
    ).length;

  <template>
    {{pageTitle (t "dashboard")}}
    {{#if this.isEligible}}
      <VStack class="gap-8">
        {{#if this.hasActiveActivity}}
          <PageHero @gradient="brand-soft" class="tio-elevation-card-raised">
            <:default>
              <div class="tio-col-extra-loose">
                <div class="tio-row-start-between">
                  <div class="max-w-2xl">
                    <div class="tio-row-tight tio-text-sm-semibold app-text-brand">
                      <PhGraduationCap class="tio-icon-sm" />
                      <span>{{this.greetingText}}</span>
                    </div>
                    <Heading @level={{1}}>
                      {{t "tuition_assistance.reimagined.dashboard.hero_title"}}
                    </Heading>
                  </div>
                  <LinkTo
                    @route="authenticated.tas.benefit-history"
                    class="tio-text-sm-semibold whitespace-nowrap app-text-link app-text-link--icon"
                  >
                    {{t "tuition_assistance.reimagined.dashboard.link_view_history"}}
                  </LinkTo>
                </div>

                <div class="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  {{#if this.showLegacyBenefitCard}}
                    <Card @radius="nested" @padding="sm">
                      <div class="tio-col">
                        <div class="tio-col-flush gap-0.5">
                          {{#if this.hasLimit}}
                            <div class="tio-text-2xl-bold tio-fg-primary">
                              {{#if this.isCreditBased}}
                                {{this.formatCredits this.remainingAmount}}
                              {{else}}
                                <CurrencySuperscriptCents @amountCents={{this.remainingAmount}} />
                              {{/if}}
                            </div>
                            <p class="text-xs tio-fg-muted">
                              {{#if this.isCreditBased}}
                                {{t "tas.dashboard.annual_benefits.estimated_remaining_credits"}}
                              {{else}}
                                {{t "tas.dashboard.annual_benefits.estimated_remaining_benefit"}}
                              {{/if}}
                            </p>
                          {{else}}
                            <div class="tio-text-2xl-bold tio-fg-primary">
                              {{#if this.isCreditBased}}
                                {{this.formatCredits this.totalUsed}}
                              {{else}}
                                <CurrencySuperscriptCents @amountCents={{this.totalUsed}} />
                              {{/if}}
                            </div>
                            <p class="text-xs tio-fg-muted">
                              {{#if this.isCreditBased}}
                                {{t "tas.dashboard.annual_benefits.total_credits_used_this_year"}}
                              {{else}}
                                {{t "tas.dashboard.annual_benefits.total_used_this_year"}}
                              {{/if}}
                            </p>
                          {{/if}}
                        </div>

                        {{#if this.hasLimit}}
                          <div
                            class="w-full h-2 tio-bg-track rounded-full overflow-hidden flex"
                            role="progressbar"
                            aria-valuenow={{this.benefitUsagePercentage}}
                            aria-valuemin={{0}}
                            aria-valuemax={{100}}
                            aria-label={{t
                              "tas.dashboard.annual_benefits.estimated_remaining_benefit"
                            }}
                          >
                            {{#if this.paidPercentage}}
                              <div
                                class="h-full app-progress-fill--brand"
                                style={{this.paidStyle}}
                              ></div>
                            {{/if}}
                            {{#if this.externalPercentage}}
                              <div
                                class="h-full app-progress-fill--brand-soft"
                                style={{this.externalStyle}}
                              ></div>
                            {{/if}}
                          </div>
                        {{/if}}

                        <div class="tio-row-between text-xs tio-fg-muted">
                          <div>
                            {{t "tas.dashboard.annual_benefits.benefit_year"}}
                            <span class="font-medium tio-fg-secondary">
                              {{formatISODateToDisplay
                                this.legacyAnnualStartDate
                                this.intl
                                "2-digit"
                              }}
                              -
                              {{formatISODateToDisplay
                                this.legacyAnnualEndDate
                                this.intl
                                "2-digit"
                              }}
                            </span>
                          </div>
                          {{#if this.hasLimit}}
                            <div>
                              <span class="font-medium">
                                {{t "tas.dashboard.annual_benefits.eligible_up_to"}}
                              </span>
                              <span class="font-semibold tio-fg-secondary">
                                {{#if this.isCreditBased}}
                                  {{this.formatCredits this.annualLimit}}
                                {{else}}
                                  {{formatCentsToDollars this.annualLimit hideCents=true}}
                                {{/if}}
                              </span>
                            </div>
                          {{/if}}
                        </div>
                      </div>
                    </Card>
                  {{/if}}
                  {{#unless @model.hasCreditCapacity}}
                    <BenefitSummaryCards
                      @variant="dashboard"
                      @employeeId={{this.currentEmployeeId}}
                      @employee={{this.sessionContext.currentEmployee}}
                      @externalSpendLabel={{this.externalSpendLabel}}
                      class="rounded-xl tio-bg-surface tio-elevation-card p-4"
                    />
                  {{/unless}}

                  <div class="grid grid-cols-2 gap-3">
                    <Card @radius="nested" @padding="xs">
                      <div class="tio-col-flush gap-0.5">
                        <div class="tio-text-overline tio-row-snug tio-fg-muted">
                          <PhBookOpen class="tio-icon-xs" />
                          {{t "tuition_assistance.reimagined.dashboard.metric_active_requests"}}
                        </div>
                        <Heading @level={{3}} @size="lg">
                          {{this.properActiveCount}}
                        </Heading>
                        <span class="text-xs leading-tight tio-fg-muted">
                          {{t
                            "tuition_assistance.reimagined.dashboard.metric_active_requests_hint"
                          }}
                        </span>
                      </div>
                    </Card>
                    <Card @radius="nested" @padding="xs">
                      <div class="tio-col-flush gap-0.5">
                        <div class="tio-text-overline tio-row-snug tio-fg-muted">
                          <PhShieldCheck class="tio-icon-xs" />
                          {{t "tuition_assistance.reimagined.dashboard.metric_tasks_required"}}
                        </div>
                        <Heading @level={{3}} @size="lg">
                          {{this.tasksRequiredCount}}
                        </Heading>
                        <span class="text-xs leading-tight tio-fg-muted">
                          {{t "tuition_assistance.reimagined.dashboard.metric_tasks_required_hint"}}
                        </span>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </:default>
          </PageHero>
        {{else}}
          <PageHero @gradient="brand-soft" class="tio-elevation-card-raised">
            <:default>
              <div class="tio-row-start-between">
                <div class="max-w-2xl">
                  <div class="tio-row-tight tio-text-sm-semibold app-text-brand">
                    <PhGraduationCap class="tio-icon-sm" />
                    <span>{{this.greetingText}}</span>
                  </div>
                  <Heading @level={{1}}>
                    {{t "tuition_assistance.reimagined.dashboard.hero_title"}}
                  </Heading>
                  <Text @variant="muted" @size="sm">
                    {{t "tuition_assistance.reimagined.dashboard.hero_subtitle"}}
                  </Text>
                  <div class="mt-4">
                    <Button @href="#eligible-benefits" @intent="primary">
                      {{t "tuition_assistance.reimagined.dashboard.cta_explore_programs"}}
                    </Button>
                  </div>
                </div>
                <LinkTo
                  @route="authenticated.tas.benefit-history"
                  class="tio-text-sm-semibold whitespace-nowrap app-text-link app-text-link--icon"
                >
                  {{t "tuition_assistance.reimagined.dashboard.link_view_history"}}
                </LinkTo>
              </div>
            </:default>
          </PageHero>
        {{/if}}

        <DataIndicator @source={{this.tasSource}} @showBadge={{true}} @dataSourceId="tas-activity">
          <Section>
            <:header>
              <div class="tio-row-between">
                {{t "tuition_assistance.reimagined.dashboard.section_activity"}}
                <div class="tio-row">
                  {{#if this.isDependentProgramsEnabled}}
                    <Button
                      @route="authenticated.tas.dependent-programs"
                      @intent="primary"
                      @appearance="outlined"
                      @size="sm"
                    >
                      {{t "common.tuition_assistance.dependent_programs"}}
                    </Button>
                  {{/if}}
                  <AddApplicationButton
                    @enrolledPrograms={{this.primedPrograms}}
                    @eligibleTemplates={{this.eligibleTemplates}}
                    @appearance="default"
                  />
                </div>
              </div>
            </:header>
            <:body>
              {{#if this.activityItems.length}}
                <div class="grid grid-cols-1 gap-4">
                  {{#each this.activityItems as |item|}}
                    {{#if (eq item.kind "application")}}
                      <ActivityCard
                        @route={{this.itemDetailRoute item}}
                        @model={{item.data.id}}
                        @name={{item.data.name}}
                        @state={{item.data.state}}
                        @paymentState={{this.itemPaymentState item}}
                        @shortId={{item.data.id}}
                        @programName={{this.itemProgramName item}}
                        @updatedAt={{item.data.updatedAt}}
                        @amountInfo={{this.itemAmountInfo item}}
                      />
                    {{else}}
                      <ActivityCard
                        @route="authenticated.tas.programs.show"
                        @model={{item.data.id}}
                        @name={{item.data.name}}
                        @state={{item.data.state}}
                        @shortId={{item.data.id}}
                        @iconKind="program"
                        @institution={{this.itemInstitution item}}
                        @updatedAt={{item.data.updatedAt}}
                      >
                        <:metrics>
                          {{#if (canAddRequests item.data.state)}}
                            <div class="hidden shrink-0 md:flex">
                              <div class="app-brand-pill">
                                <span class="tio-text-micro font-medium app-text-muted">
                                  {{t
                                    "tuition_assistance.reimagined.dashboard.activity_requests_label"
                                  }}
                                </span>
                                <div class="tio-text-xs-bold app-text-primary">
                                  {{this.programRequestCount item.data.id}}
                                </div>
                                <span class="tio-text-micro app-brand-pill__cta">
                                  {{t
                                    "tuition_assistance.reimagined.dashboard.activity_requests_add_now"
                                  }}
                                </span>
                              </div>
                            </div>
                          {{/if}}
                        </:metrics>
                      </ActivityCard>
                    {{/if}}
                  {{/each}}
                </div>
              {{else}}
                <div class="rounded-2xl border border-dashed tio-border-default tio-bg-sunken p-8">
                  <Heading @level={{3}}>{{t
                      "tuition_assistance.reimagined.dashboard.empty_activity_title"
                    }}</Heading>
                  <Text @variant="muted">
                    {{t "tuition_assistance.reimagined.dashboard.empty_activity_body"}}
                  </Text>
                  <div class="mt-4">
                    <Button @href="#eligible-benefits" @intent="primary">
                      {{t "tuition_assistance.reimagined.dashboard.cta_explore_programs"}}
                    </Button>
                  </div>
                </div>
              {{/if}}
            </:body>
          </Section>
        </DataIndicator>

        <AddApplicationButton
          class="md:px-4"
          @enrolledPrograms={{this.primedPrograms}}
          @eligibleTemplates={{this.eligibleTemplates}}
        />

        <DataIndicator
          @source={{this.tasSource}}
          @showBadge={{true}}
          @dataSourceId="tas-eligible-benefits"
        >
          <Section id="eligible-benefits">
            <:header>
              {{t "tas.dashboard.eligible_benefits"}}
            </:header>
            <:actions>
              {{#if this.eligibleTemplates.length}}
                <Select
                  class="min-w-40"
                  @items={{this.eligibleSortOptions}}
                  @selected={{this.eligibleSort}}
                  @onChange={{this.handleEligibleSortChange}}
                  @clearable={{false}}
                  @size="sm"
                  @label={{t "tas.dashboard.sort"}}
                  @testHook="tas-dashboard__eligible-benefits-sort"
                />
              {{/if}}
            </:actions>
            <:body>
              {{#if this.eligibleTemplates.length}}
                <div class="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {{#each this.sortedEligibleTemplates as |template|}}
                    <Card
                      @padding="sm"
                      @radius="nested"
                      class="tio-col tio-elevation-card-hover"
                      data-test-tas-benefit-card
                    >
                      <div class="tio-flex-end gap-2 min-h-5">
                        {{#if template.isEnrolled}}
                          <Chip @appearance="faded" @intent="success" @size="sm">
                            {{t "tas.dashboard.chips.enrolled"}}
                          </Chip>
                        {{/if}}
                        {{#if template.isDirectPayProgram}}
                          <Chip @appearance="faded" @intent="primary" @size="sm">
                            {{htmlSafe (t "common.tas.course_cash.default")}}
                          </Chip>
                        {{/if}}
                        {{#if template.isTrial}}
                          <Chip @appearance="faded" @intent="warning" @size="sm">
                            {{t "tas.dashboard.chips.trial"}}
                          </Chip>
                        {{/if}}
                      </div>
                      <div class="tio-col-extra-tight">
                        <Heading @level={{3}} @size="base">{{template.name}}</Heading>
                        {{#if template.description}}
                          <Text @variant="muted" @size="sm" class="line-clamp-2">
                            {{template.description}}
                          </Text>
                        {{/if}}
                      </div>
                      <div>
                        <LinkTo
                          @route="authenticated.tas.getting-started"
                          @model={{template.id}}
                          class="tio-text-sm-semibold app-text-link"
                        >
                          {{t "tas.dashboard.see_more"}}
                          <PhArrowRight class="tio-icon-xs ml-1 inline" />
                        </LinkTo>
                      </div>
                    </Card>
                  {{/each}}
                </div>
              {{else}}
                <div class="tio-row-tight tio-fg-disabled">
                  <PhCompass class="tio-icon-md shrink-0" />
                  <Text @variant="muted">{{t "tas.dashboard.no_eligible_programs"}}</Text>
                </div>
              {{/if}}
            </:body>
          </Section>
        </DataIndicator>

        <TasDashboardLpnPromo @eligibleTemplates={{this.eligibleTemplates}} />
      </VStack>
    {{else}}
      <VStack class="gap-8">
        <PageHero @gradient="neutral" class="tio-tas-ineligible-hero">
          <:default>
            <div class="tio-col-extra-loose lg:flex-row lg:items-center lg:justify-between">
              <div class="max-w-2xl">
                <div class="tio-row-tight tio-text-sm-semibold">
                  <PhLock class="tio-icon-sm" />
                  <span>{{t "tuition_assistance.default"}}</span>
                </div>
                <Heading @level={{1}} class="tio-fg-inverse">
                  {{t "tuition_assistance.reimagined.dashboard.ineligible_title"}}
                </Heading>
                <Text @variant="inherit" class="tio-tas-ineligible-hero__copy">
                  {{t "tuition_assistance.reimagined.dashboard.ineligible_body"}}
                </Text>
              </div>
              <div class="tio-tas-ineligible-hero__icon-well">
                <PhGraduationCap class="tio-tas-ineligible-hero__icon" />
              </div>
            </div>
          </:default>
        </PageHero>

        {{! Demo sessions only: these cards deep-link into the reimagined
            sandbox, which is a demo/dev surface, not a production
            destination (#8022) — a real ineligible user must not land there. }}
        {{#if this.demoMode.isActive}}
          <Section>
            <:header>
              {{t "tuition_assistance.reimagined.dashboard.ineligible_explore_title"}}
            </:header>
            <:body>
              <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                <LinkTo @route="authenticated.reimagined.loans" class="block">
                  <ExploreCard
                    @icon={{PhBank}}
                    @intent="success"
                    @titleKey="tuition_assistance.reimagined.dashboard.ineligible_slr_title"
                    @bodyKey="tuition_assistance.reimagined.dashboard.ineligible_slr_body"
                  />
                </LinkTo>
                <LinkTo @route="authenticated.reimagined.benefits" class="block">
                  <ExploreCard
                    @icon={{PhCompass}}
                    @intent="brand"
                    @titleKey="tuition_assistance.reimagined.dashboard.ineligible_all_benefits_title"
                    @bodyKey="tuition_assistance.reimagined.dashboard.ineligible_all_benefits_body"
                  />
                </LinkTo>
              </div>
            </:body>
          </Section>
        {{/if}}

        <div
          class="rounded-2xl border border-dashed tio-border-default tio-bg-sunken p-8 text-center"
        >
          <PhGraduationCap class="mx-auto h-12 w-12 tio-fg-muted" />
          <Heading @level={{3}} class="mt-4">{{t
              "tuition_assistance.reimagined.dashboard.ineligible_interested_title"
            }}</Heading>
          <Text @variant="muted" class="mt-2 max-w-lg mx-auto">
            {{t "tuition_assistance.reimagined.dashboard.ineligible_interested_body"}}
          </Text>
        </div>
      </VStack>
    {{/if}}
  </template>
}
